import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Subscription, of } from 'rxjs';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { ToolsService } from 'src/app/service/tools/tools.service';
import { SelectedDataModel, InventoryTransferModel, TransferModelData } from './tool-inventory-transfer.model';

@Injectable({
  providedIn: 'root',
})
export class ToolInventoryTransferService {
  private selectedInventoryItemsSource = new BehaviorSubject<Array<SelectedDataModel>>([]);
  selectedInventoryItems = this.selectedInventoryItemsSource.asObservable();

  private inventoryTransferModalIsOpenSource = new BehaviorSubject<boolean>(false);
  inventoryTransferModalIsOpen = this.inventoryTransferModalIsOpenSource.asObservable();

  private submitSubscriberSource = new BehaviorSubject<any>({ next: null, error: null });
  submitSubscriber = this.submitSubscriberSource.asObservable();

  private refreshToolsSubscriberSource = new BehaviorSubject<any>({ next: null, error: null });
  refreshToolsSubscriber = this.refreshToolsSubscriberSource.asObservable();

  defaultInventoryTransferModel: InventoryTransferModel = {
    toolId: null,
    assignedDate: null,
    notes: null,
  };

  inventoryTransferModel: InventoryTransferModel = this.defaultInventoryTransferModel;
  inventoryTransferSubscription: Subscription;

  constructor(
    private toolsService: ToolsService,
    private toastService: ToastService,
  ) { }

  transfer(item: SelectedDataModel, refreshSubscription: Subscription = undefined) {
    this.inventoryTransferSubscription = refreshSubscription;
    this.inventoryTransferModalIsOpenSource.next(true);

    if (item) {
      this.add(item);
    }
  }

  batchTransfer(refreshSubscription: Subscription = undefined) {
    this.inventoryTransferSubscription = refreshSubscription;
    this.inventoryTransferModalIsOpenSource.next(true);
  }

  batchAdd(items: Array<SelectedDataModel>) {
    const currentList = this.selectedInventoryItemsSource.value;
    let updatedList = [...currentList, ...items];
    updatedList = this.filterDuplicates(updatedList);
    this.selectedInventoryItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  batchRemove(items: Array<SelectedDataModel>) {
    const currentList = this.selectedInventoryItemsSource.value;
    const updatedList = this.filterRemovable(currentList, items);
    this.selectedInventoryItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  add(item: SelectedDataModel, promisify: boolean = false) {
    const itemIsToolInventoryJunction = !!item?.toolInventoryId
    const lookupId = itemIsToolInventoryJunction ? item.toolId : item.id

    if (promisify) {
      return new Promise((resolve, reject) => {
        this.toolsService.readById
          .tools(lookupId, {
            cache: 'clear',
            embed: 'assignment,alert,model',
          })
          .subscribe(
            res => {
              const { data } = res;
              this.remove(data.id);
              const currentList = this.selectedInventoryItemsSource.value;
              let updatedList = [{ ...data, toolInventoryJunctionId: item.id }, ...currentList];
              updatedList = this.filterDuplicates(updatedList);
              this.selectedInventoryItemsSource.next(updatedList);
              this.saveToLocalStorage();
              resolve(true);
            },
            error => reject(error),
          );
      });
    }

    this.toolsService.readById
      .tools(lookupId, {
        cache: 'clear',
        embed: 'assignment,alert,model',
      })
      .subscribe(
        res => {
          const { data } = res;
          this.remove(data.id)
          const currentList = this.selectedInventoryItemsSource.value;
          let updatedList = [{ ...data, toolInventoryJunctionId: item.id }, ...currentList];
          updatedList = this.filterDuplicates(updatedList);

          this.selectedInventoryItemsSource.next(updatedList);
          this.saveToLocalStorage();
        },
        error => console.error('error coming from transfer subscription', error),
      );
  }

  get(id: number) {
    const currentList = this.selectedInventoryItemsSource.value;
    return currentList.find(row => row.id === id);
  }

  getUpdatedListForRemove(id: number) {
    const currentList = this.selectedInventoryItemsSource.value;
    return currentList.filter(row => row.id !== id);
  }

  remove(id: number) {
    const updatedList = this.getUpdatedListForRemove(id)
    this.selectedInventoryItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  clear() {
    this.clearModelData();
    this.selectedInventoryItemsSource.next([]);
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    localStorage.setItem('selected-inventory-items', JSON.stringify(this.selectedInventoryItemsSource.value));
  }

  loadFromLocalStorage() {
    const data = JSON.parse(localStorage.getItem('selected-inventory-items')) || [];
    this.selectedInventoryItemsSource.next(data);
  }

  filterDuplicates(list: Array<SelectedDataModel>) {
    return list.filter((e, i) => list.findIndex(obj => obj.id == e.id) === i);
  }

  filterRemovable(a: Array<SelectedDataModel>, b: Array<SelectedDataModel>) {
    return a.filter(e => !b.find(f => f.id === e.id));
  }

  async submitTransfer(transferKey: string) {
    if (!this.getIds().length) {
      return this.toastService.showToast('Select at least one tool first', 'error');
    }

    const batchId = await this.toolsService.generateTransferBatchID();

    const modelData = this.getModelData();
    const requests = modelData.map(payload => {
      const queryString = `batchId=${batchId}`;
      return this.toolsService.create['assignment'](payload, queryString);
    });

    forkJoin(requests).subscribe(
      next => this.handleSuccess(next, transferKey),
      error => this.handleError(error),
    );
  }

  handleSuccess(next, transferKey: string) {
    this.refreshToolsSubscriberSource.next({ next });
    this.toastService.showToast('Tools transferred successfully', 'success');
    this.clear();
    if (transferKey === 'close') {
      this.close();
    }
    this.submitSubscriberSource.next({ next });
  }

  handleError(err) {
    this.toastService.showToast('Something went wrong', 'error');
    this.submitSubscriberSource.next({ err });
  }

  addModelData(model: TransferModelData) {
    const { key, value } = model;
    this.inventoryTransferModel[key] = value;
  }

  removeModelData(model: TransferModelData) {
    const { key } = model;
    delete this.inventoryTransferModel[key];
  }

  clearModelData() {
    const { assignedDate } = this.inventoryTransferModel;
    this.inventoryTransferModel = { ...this.defaultInventoryTransferModel };
    this.inventoryTransferModel.assignedDate = assignedDate;
  }

  getModelData() {
    return this.getIds().map((id: number) => {
      return {
        ...this.inventoryTransferModel,
        toolId: id,
      };
    });
  }

  getIds() {
    return this.selectedInventoryItemsSource.value.map((item: SelectedDataModel) => item.id);
  }

  close() {
    if (this.inventoryTransferSubscription) this.inventoryTransferSubscription.unsubscribe();
    this.inventoryTransferModalIsOpenSource.next(false);
  }

  open() {
    this.inventoryTransferModalIsOpenSource.next(true);
  }

  set(items: Array<SelectedDataModel>) {
    this.selectedInventoryItemsSource.next(items);
  }
}
